import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Loader from '../../components/Loader';
import { toast } from 'react-toastify';
import ApiService from '../../services/ApiService';
import ImageUpload from '../../components/ImageUpload';
import { formatDate } from '../../utils/helper';

const AddUpdate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [pictureFile, setPictureFile] = useState(null);
  const [pictureUrl, setPictureUrl] = useState(null);
  const [insuranceTypes, setInsuranceTypes] = useState([]);
  const [phoneCode, setPhoneCode] = useState('+255');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    insurance_type_id: '',
    gender: 'male',
    date_of_birth: '',
    address: '',
    status: true,
    password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (id) {
      getSingleData();
    }
    getInsuranceTypes();
  }, [id]);

  const getSingleData = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/patients/${id}`,
      });
      const data = response.data;
      if (data.status) {
        const userData = data.data; // Assuming key is 'patient'
        
        let pCode = '+255';
        let pNumber = userData.phone || '';
        if (userData.phone && userData.phone.startsWith('+')) {
             // Simple extraction, assuming code is 4 chars max? 
             // Better: check if starts with known codes. 
             // For now, let's assume if it starts with +, the first 4 chars are code if generic, 
             // OR just handle +255 specifically.
             if(userData.phone.startsWith('+255')) {
                 pCode = '+255';
                 pNumber = userData.phone.substring(4);
             } else {
                 // Fallback or other codes
                 // If we support other codes, we need logic. 
                 // For now, let's split by length or just take +xxx
                 const match = userData.phone.match(/^(\+\d{1,4})(\d+)$/);
                 if (match) {
                     pCode = match[1];
                     pNumber = match[2];
                 }
             }
        }

        setPhoneCode(pCode);

        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          phone: pNumber,
          insurance_type_id: userData.insurance_type_id || '',
          gender: userData.gender || 'male',
          date_of_birth: formatDate(userData.date_of_birth,'YYYY-MM-DD') || '',
          address: userData.address || '',
          status: userData.status !== undefined ? userData.status : true,
          password: '',
          confirm_password: '',
        });
        setPictureUrl(userData.picture || null);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load patient data');
    } finally {
      setIsLoader(false);
    }
  };

  const getInsuranceTypes = async () => {
    setIsLoader(true);
    try {
      const response = await ApiService.request({
        method: 'GET',
        url: `admin/insurance-types`,
        data:{per_page:100}
      });
      const data = response.data;
      if (data.status) {
        const userData = data.data.data; // Assuming key is 'insurance_types'
        // console.log(userData);
        setInsuranceTypes(userData || []);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load insurance types');
    } finally {
      setIsLoader(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (name, file) => {
    if (name === 'picture') {
      setPictureFile(file);
      if (file) {
        setPictureUrl(URL.createObjectURL(file));
      } else {
        setPictureUrl(null);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    

    setIsLoader(true);
    try {
      const data = new FormData();
      
      // Combine phone code and number
      const fullPhone = phoneCode + formData.phone;
      
      Object.keys(formData).forEach((key) => {
        if (key === 'phone') {
            data.append('phone', fullPhone);
        } else {
            data.append(key, formData[key]);
        }
      });

      if (pictureFile) {
        data.append('picture', pictureFile);
      }

      if (id) {
          data.append('_method', 'PUT');
      }

      const response = await ApiService.request({
        method: 'POST', // Usually POST for both create and update with FormData in Laravel/PHP APIs to handle file uploads properly, or using _method=PUT
        url: id ? `admin/patients/${id}` : 'admin/patients',
        data: data,
        headers: {
            'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.status) {
        toast.success(response.data.message);
        navigate('/patients');
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Something went wrong');
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              {id ? 'Edit Patient' : 'Add Patient'}
            </h1>
          </div>
          <div className="d-flex align-items-center gap-2 gap-lg-3">
            <Link to="/patients" className="btn btn-sm fw-bold btn-secondary">
              Back
            </Link>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-fluid">
          <div className="card">
            <div className="card-body py-4">
              {isLoader && !id ? (
                <Loader />
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Name</label>
                      <input
                        type="text"
                        name="name"
                        className="form-control form-control-solid"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control form-control-solid"
                        value={formData.email}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Phone</label>
                      <div className="input-group">
                        <input
                            type="text"
                            className="form-control form-control-solid w-80px"
                            value={phoneCode}
                            onChange={(e) => setPhoneCode(e.target.value)}
                            placeholder="+255"
                            style={{ maxWidth: '80px' }}
                        />
                        <input
                          type="text"
                          name="phone"
                          className="form-control form-control-solid"
                          value={formData.phone}
                          onChange={(e) => {
                             const val = e.target.value.replace(/\D/g, '');
                             setFormData({...formData, phone: val});
                          }}
                          required
                          placeholder="Phone Number"
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Insurance Type (Optional)</label>
                      <select
                        name="insurance_type_id"
                        className="form-control form-control-solid"
                        value={formData.insurance_type_id}
                        onChange={handleChange}
                      >
                        <option value="">Select Insurance Type</option>
                        {insuranceTypes && insuranceTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                                {type.title}
                            </option>
                        ))}
                      </select>
                    </div>
                    {formData.insurance_type_id && (
                    <div className="col-md-6 mb-3">
                      <label className="form-label required">Insurance Card Number</label>
                      <input
                        type="text"
                        name="insurance_card_number"
                        className="form-control form-control-solid"
                        value={formData.insurance_card_number}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    )}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Gender</label>
                      <select
                        name="gender"
                        className="form-control form-control-solid"
                        value={formData.gender}
                        onChange={handleChange}
                      >
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                     <div className="col-md-6 mb-3">
                      <label className="form-label">Date of Birth</label>
                      <input
                        type="date"
                        name="date_of_birth"
                        className="form-control form-control-solid"
                        max={new Date().toISOString().split('T')[0]}
                        value={formData.date_of_birth}
                        onChange={handleChange}
                      />
                    </div>
                     <div className="col-md-6 mb-3">
                      <label className="form-label">Address</label>
                      <input
                        type="text"
                        name="address"
                        className="form-control form-control-solid"
                        value={formData.address}
                        onChange={handleChange}
                      />
                    </div>
                    
                    {/* {!id && (
                        <>
                            <div className="col-md-6 mb-3">
                            <label className="form-label required">Password</label>
                            <input
                                type="password"
                                name="password"
                                className="form-control form-control-solid"
                                value={formData.password}
                                onChange={handleChange}
                                required
                            />
                            </div>
                            <div className="col-md-6 mb-3">
                            <label className="form-label required">Confirm Password</label>
                            <input
                                type="password"
                                name="confirm_password"
                                className="form-control form-control-solid"
                                value={formData.confirm_password}
                                onChange={handleChange}
                                required
                            />
                            </div>
                        </>
                    )} */}

                    <div className="col-md-6 mb-3">
                      <label className="form-check form-check-custom form-check-solid form-check-inline mt-8">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="status"
                          checked={formData.status}
                          onChange={handleChange}
                        />
                        <span className="form-check-label fw-bold text-gray-700 fs-6">Active Status</span>
                      </label>
                    </div>

                    <div className="col-md-6 my-3 ">
                      <label className="form-label">Picture</label>

                       <ImageUpload
                            name="picture"
                            label="Profile Picture"
                            imageUrl={pictureUrl}
                            onImageChange={handleImageChange}
                        />
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <button type="submit" className="btn btn-base" disabled={isLoader}>
                      {isLoader ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUpdate;
